import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { PersonSearchResult } from '@/apiTypes/types';
import SearchPersonsList from '@/components/search/content/SearchPersonsList';
import Paginator from '@/components/ui/Paginator';
import { SearchScope } from '@/lib/enums/searchScope';
import { useTRPC } from '@/trpc/client';
import { searchMoreQueryOptions } from '@/lib/queries/searchQueries';
import { useToaster } from '@/components/hooks/useToaster';

const SearchPersonsContent: React.FC<PersonSearchResult> = ({ items, pagination }) => {
  const [currentItems, setCurrentItems] = useState(items);
  const [currentPagination, setCurrentPagination] = useState(pagination);
  const numberOfPages = Math.ceil(currentPagination.total / currentPagination.limit);
  const currentPage = Math.floor(currentPagination.from / currentPagination.limit) + 1;
  const search = useSearchParams();
  const { t } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { openToast } = useToaster();

  React.useEffect(() => {
    setCurrentItems(items);
    setCurrentPagination(pagination);
  }, [items, pagination]);

  const handlePageChangeAsync = async (selected: number) => {
    const selectedFrom = selected * currentPagination.limit;
    try {
      const result = await queryClient.fetchQuery(
        searchMoreQueryOptions({
          trpc,
          query: search.get('query') ?? undefined,
          scope: SearchScope.persons,
          from: selectedFrom,
          limit: currentPagination.limit,
        }),
      );
      setCurrentItems(result.persons.items);
      setCurrentPagination(result.persons.pagination);
    } catch (error) {
      console.error('failed to load person search page', error);
      openToast({
        id: 'search-page-load-failed',
        text: t('components.error.title'),
        description: t('components.error.description'),
        intent: 'danger',
      });
    }
  };

  const handlePageChange = ({ selected }: { selected: number }) => {
    void handlePageChangeAsync(selected);
  };

  return (
    <React.Fragment>
      <table className="wrappable">
        <thead>
          <tr>
            <th>{t('person.table.name')}</th>
            <th className="align-right">{t('person.table.birthday')}</th>
          </tr>
        </thead>
        <tbody>
          <SearchPersonsList persons={currentItems} />
        </tbody>
      </table>
      <Paginator
        numberOfPages={numberOfPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </React.Fragment>
  );
};

export default SearchPersonsContent;
